import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { CheckCircle, ServerCrash } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { useModalProvider } from '@/common/hooks/useModalProvider'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'

async function deleteAccount(id: string) {
  return api.backend.delete(`/account/${id}`)
}

export function useInactivateAccount() {
  const { t } = useTranslation('sso')
  const { handleModal, toggleLoading } = useModalProvider()

  const { mutateAsync, isPending } = useMutation<
    unknown,
    AxiosError<ApiError>,
    string
  >({
    mutationFn: deleteAccount,
    onSuccess: () => {
      toast(Toast, {
        data: {
          type: 'success',
          icon: CheckCircle,
          title: t('accounts.use_inactivate_account.toast_success.title'),
        },
      })
      queryClient.invalidateQueries({ queryKey: ['/account/paginate'] })
    },
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: ServerCrash,
          title: t('accounts.use_inactivate_account.toast_error.title'),
          description:
            error?.response?.data?.friendlyMessage ||
            t('accounts.use_inactivate_account.toast_error.description'),
        },
      })
    },
  })

  const handleInactivateAccount = async (id: string) => {
    handleModal({
      type: 'danger',
      title: t('accounts.use_inactivate_account.modal.title'),
      description: t('accounts.use_inactivate_account.modal.description'),
      confirmText: t('accounts.use_inactivate_account.modal.button_confirm'),
      onConfirm: async () => {
        toggleLoading()
        await mutateAsync(id).finally(() => {
          toggleLoading()
        })
      },
    })
  }

  return {
    handleInactivateAccount,
    isInactivatingAccount: isPending,
  }
}
